import {
  ChatMessage,
  queueChatMessage,
  sendAttachmentQueue,
} from '@reducers/chatMessageQueue';
import useAttachFilesSummary from '@src/hooks/useAttachFilesSummary';
import { MediaType } from '@src/model/frontendmodel';
import { changeWrittenMessage } from '@src/reducers/chats';
import { sendQuickMessageWithFiles } from '@src/reducers/quickMessages';
import { isComposerToolbarDisabled } from '@src/selectors/agentConsole';
import {
  activeChatSelector,
  activeWrittenMessageSelector,
} from '@src/selectors/chats';
import { RootState } from '@src/store/reduxstore';
import { maximumAllowedCharacters } from '@src/utils/validation';
import { DebouncedFunc } from 'lodash';
import React, { RefObject, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ComposerTextInput from '../ComposerTextInput/index';
import { SendButton } from '../SendButton';
import attachFilesUtil from './FileUtils';

interface IComposerAndSendButtonWrapper {
  textInputRef: RefObject<HTMLDivElement | null>;
  getQuickMessages: DebouncedFunc<(text: string) => void>;
  maxRows: number;
}

const ComposerAndSendButtonWrapper: React.FC<IComposerAndSendButtonWrapper> = ({
  textInputRef,
  getQuickMessages,
  maxRows,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [totalAllowedCharacters, setTotalAllowedCharacters] = useState(0);
  const [maxCharactersExceeded, setMaxCharactersExceeded] = useState(false);
  const [openImagePreview, setOpenImagePreview] = useState(false);

  const activeChat = useSelector(activeChatSelector);
  const activeWrittenMessage = useSelector(activeWrittenMessageSelector);
  const composerToolBarDisabled = useSelector(isComposerToolbarDisabled);
  const isTherePendingQuickMessagesFiles = useSelector(
    (state: RootState) => state.quickMessages.queueFiles.length > 0
  );

  const { isAttachFilesSummaryEnabled } = useAttachFilesSummary();

  const resetTotalAllowedCharacters = () => {
    if (activeChat?.provider) {
      setMaxCharactersExceeded(false);
      setTotalAllowedCharacters(maximumAllowedCharacters(activeChat?.provider));
    }
  };

  const sendTextMessage = () => {
    if (activeWrittenMessage?.length) {
      dispatch(
        changeWrittenMessage({
          chatId: activeChat?.id,
          message: '',
        })
      );
      const message: ChatMessage = {
        type: MediaType.TEXT,
        text: activeWrittenMessage,
      };

      if (isTherePendingQuickMessagesFiles) {
        dispatch(sendQuickMessageWithFiles(activeWrittenMessage));
      } else {
        dispatch(
          queueChatMessage(message, activeChat?.id, activeChat?.provider)
        );
      }
    } else if (isTherePendingQuickMessagesFiles) {
      dispatch(sendQuickMessageWithFiles(''));
    }

    resetTotalAllowedCharacters();
  };

  const handleSendAttachmentQueue = () => {
    if (activeChat?.id)
      dispatch(sendAttachmentQueue({ chatId: activeChat?.id }));
  };

  const handleSendMessage = () => {
    sendTextMessage();
    handleSendAttachmentQueue();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && openImagePreview === true) {
      setOpenImagePreview(false);
    }
  };

  const handleWrittenMessageKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendTextMessage();
    }
  };

  const handleWrittenMessageChanged = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const chatId = activeChat?.id;

    let text = event.target.value;

    setMaxCharactersExceeded(text.length > totalAllowedCharacters);

    if (totalAllowedCharacters !== -1) {
      text = text.slice(0, totalAllowedCharacters);
    }

    dispatch(changeWrittenMessage({ chatId, message: text }));
    getQuickMessages(text);
  };

  const sendAudioRecording = (buffer: BlobPart[]) => {
    const file = new File(buffer, `${uuidv4()}.mp3`, {
      type: 'audio/ogg',
      lastModified: Date.now(),
    });
    attachFilesUtil({
      files: [file],
      activeChat,
      formatMessage,
      isAttachFilesSummaryEnabled,
    });
  };

  useEffect(() => {
    if (activeChat.id && !composerToolBarDisabled) {
      textInputRef?.current?.focus();
    }

    resetTotalAllowedCharacters();

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeChat.id && !composerToolBarDisabled) {
      textInputRef?.current?.focus();
      getQuickMessages(activeWrittenMessage);
    }

    resetTotalAllowedCharacters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat]);

  useEffect(() => {
    if (activeChat?.provider && totalAllowedCharacters === -1) {
      resetTotalAllowedCharacters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat, totalAllowedCharacters]);

  return (
    <>
      <ComposerTextInput
        writtenMessage={activeWrittenMessage}
        maxCharactersExceeded={maxCharactersExceeded}
        helperText={formatMessage(
          {
            id: 'ChatMessageComposer.maximumCharactersExceeded',
          },
          { maximum: totalAllowedCharacters }
        )}
        placeholder={formatMessage({ id: 'ChatMessageComposer.hintText' })}
        maxRows={maxRows}
        typeMessageDisabled={composerToolBarDisabled}
        textInputRef={textInputRef}
        totalAllowedCharacters={totalAllowedCharacters}
        handleWrittenMessageChanged={handleWrittenMessageChanged}
        handleWrittenMessageKeyDown={event =>
          handleWrittenMessageKeyDown(event as unknown as KeyboardEvent)
        }
      />
      <SendButton
        sendAudioRecording={sendAudioRecording}
        handleSendMessage={handleSendMessage}
      />
    </>
  );
};

export default ComposerAndSendButtonWrapper;
